/**
 * Client-side price math for the retreat booking modal — a port of the tenant
 * side `booking-modal/pricing-client.ts` (itself a mirror of the server's
 * Decimal-based `lib/retreats/pricing.ts`).
 *
 * The server remains the source of truth at the actual booking; this is ONLY
 * for the live PriceSummary + payment-option amounts shown in the modal.
 */

import type { PaymentMode } from '../types'

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

function floor2(value: number): number {
  return Math.floor(value * 100) / 100
}

export interface PriceBreakdown {
  pricePerPerson: number
  participantCount: number
  subtotal: number
  vatAmount: number
  discountAmount: number
  totalPrice: number
  currency: string
}

export interface PriceInput {
  pricePerPerson: number
  participantCount: number
  vatRate: number
  vatExempt: boolean
  currency: string
  discountAmount?: number
}

export function calculatePrice(input: PriceInput): PriceBreakdown {
  const pricePerPerson = input.pricePerPerson
  const subtotal = pricePerPerson * input.participantCount

  let vatAmount = 0
  if (!input.vatExempt) {
    const rate = input.vatRate
    if (rate > 0) {
      vatAmount = (subtotal * rate) / (100 + rate)
    }
  }

  const discount = input.discountAmount ?? 0
  const totalPrice = Math.max(0, subtotal - discount)

  return {
    pricePerPerson,
    participantCount: input.participantCount,
    subtotal: round2(subtotal),
    vatAmount: round2(vatAmount),
    discountAmount: round2(discount),
    totalPrice: round2(totalPrice),
    currency: input.currency,
  }
}

export interface PaymentSplit {
  amountNow: number
  amountDueLater: number
}

export function splitPayment(
  total: number,
  mode: PaymentMode,
  options: { depositPercent?: number | null; installmentsCount?: number | null } = {},
): PaymentSplit {
  if (mode === 'free' || total === 0) {
    return { amountNow: 0, amountDueLater: 0 }
  }
  if (mode === 'full') {
    return { amountNow: round2(total), amountDueLater: 0 }
  }
  if (mode === 'deposit') {
    const pct = options.depositPercent
    if (!pct || pct <= 0 || pct >= 100) {
      return { amountNow: round2(total), amountDueLater: 0 }
    }
    const deposit = round2((total * pct) / 100)
    return { amountNow: deposit, amountDueLater: round2(total - deposit) }
  }
  if (mode === 'installments') {
    const count = options.installmentsCount
    if (!count || count < 2 || count > 12) {
      return { amountNow: round2(total), amountDueLater: 0 }
    }
    const perInstallment = floor2(total / count)
    return { amountNow: perInstallment, amountDueLater: round2(total - perInstallment) }
  }
  return { amountNow: round2(total), amountDueLater: 0 }
}

const LOCALE_MAP: Record<string, string> = {
  da: 'da-DK',
  en: 'en-GB',
  sv: 'sv-SE',
  nb: 'nb-NO',
  de: 'de-DE',
  es: 'es-ES',
}

/** Retreat price with 2 decimals (splits can be non-round). Matches the tenant
 *  modal's formatRetreatPrice. */
export function formatRetreatPrice(amount: number, currency: string, locale: string): string {
  const bcp = LOCALE_MAP[locale] ?? locale ?? 'da-DK'

  if (currency === 'DKK' && locale === 'da') {
    const formatted = new Intl.NumberFormat('da-DK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
    return `${formatted} kr`
  }

  try {
    return new Intl.NumberFormat(bcp, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

export interface PriceSummaryComputed {
  breakdown: PriceBreakdown
  split: PaymentSplit
}

export function computePriceSummary(args: {
  retreat: {
    vatRate: number
    vatExempt: boolean
    currency: string
    depositPercent: number | null
    installmentsCount: number | null
  }
  tierPrice: number
  participantCount: number
  paymentMode: PaymentMode
  discountAmount?: number
}): PriceSummaryComputed {
  const breakdown = calculatePrice({
    pricePerPerson: args.tierPrice,
    participantCount: args.participantCount,
    vatRate: args.retreat.vatRate,
    vatExempt: args.retreat.vatExempt,
    currency: args.retreat.currency,
    discountAmount: args.discountAmount,
  })
  const split = splitPayment(breakdown.totalPrice, args.paymentMode, {
    depositPercent: args.retreat.depositPercent,
    installmentsCount: args.retreat.installmentsCount,
  })
  return { breakdown, split }
}
